import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { setConsent as setConsentStore, getConsent as getConsentStore, clearConsent } from '../lib/consent';

const COOKIE_CONSENT_KEY = 'mid2bid_cookie_consent';

export default function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be changed
    functional: true,
    analytics: true
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Delay showing banner slightly for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
      } catch {
        setIsVisible(true);
      }
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    // Mirror into the canonical consent store that gates analytics (Phase 2.6)
    setConsentStore({
      functional: !!prefs.functional,
      analytics: !!prefs.analytics,
      marketing: false,
    });
    setIsVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      timestamp: new Date().toISOString()
    };
    savePreferences(allAccepted);
  };

  const rejectNonEssential = () => {
    const essentialOnly = {
      necessary: true,
      functional: false,
      analytics: false,
      timestamp: new Date().toISOString()
    };
    savePreferences(essentialOnly);
  };

  const saveCustomPreferences = () => {
    savePreferences({
      ...preferences,
      timestamp: new Date().toISOString()
    });
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 animate-in slide-in-from-bottom duration-500 pointer-events-none">
      <div className="max-w-4xl mx-auto pointer-events-auto">
        <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
          {!showSettings ? (
            // Main Banner
            <div className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Cookie className="w-6 h-6 text-accent" />
                  </div>
                </div>
                <div className="flex-grow">
                  <h3 className="font-semibold text-foreground mb-1">We value your privacy</h3>
                  <p className="text-sm text-muted-foreground">
                    We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                    By clicking "Accept All", you consent to our use of cookies. Read our{' '}
                    <Link to="/cookie-policy" className="text-primary hover:underline">Cookie Policy</Link> and{' '}
                    <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link> for more information.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSettings(true)}
                    className="w-full sm:w-auto"
                    data-testid="cookie-settings-btn"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={rejectNonEssential}
                    className="w-full sm:w-auto"
                    data-testid="cookie-reject-btn"
                  >
                    Reject All
                  </Button>
                  <Button
                    size="sm"
                    onClick={acceptAll}
                    className="w-full sm:w-auto bg-accent hover:bg-accent/90"
                    data-testid="cookie-accept-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Accept All
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            // Settings Panel
            <div className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Cookie Preferences
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSettings(false)}
                  className="h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-sm">Strictly Necessary</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Essential for the website to function. Cannot be disabled.
                    </p>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="sr-only"
                    />
                    <div className="w-10 h-5 bg-accent rounded-full cursor-not-allowed opacity-70">
                      <div className="absolute top-0.5 left-5 w-4 h-4 bg-white rounded-full shadow"></div>
                    </div>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-sm">Functional</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Remember your preferences like language and theme settings.
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, functional: !prev.functional }))}
                    className="relative"
                    data-testid="functional-cookie-toggle"
                  >
                    <div className={`w-10 h-5 rounded-full transition-colors ${preferences.functional ? 'bg-accent' : 'bg-muted-foreground/30'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${preferences.functional ? 'left-5' : 'left-0.5'}`}></div>
                    </div>
                  </button>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-sm">Analytics</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Help us understand how visitors interact with our website.
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))}
                    className="relative"
                    data-testid="analytics-cookie-toggle"
                  >
                    <div className={`w-10 h-5 rounded-full transition-colors ${preferences.analytics ? 'bg-accent' : 'bg-muted-foreground/30'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${preferences.analytics ? 'left-5' : 'left-0.5'}`}></div>
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={rejectNonEssential}
                >
                  Reject All
                </Button>
                <Button
                  size="sm"
                  onClick={saveCustomPreferences}
                  className="bg-accent hover:bg-accent/90"
                  data-testid="save-cookie-preferences"
                >
                  Save Preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Export a function to open cookie settings programmatically
export function openCookieSettings() {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  clearConsent();
  window.location.reload();
}
